import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import {
  doc, getDoc, collection, query, where, getDocs
} from "firebase/firestore";
import {
  Box, Container, Grid, Paper, Typography, Chip, Divider, List,
  ListItemButton, ListItemText, Avatar, CircularProgress, Alert
} from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";

// ---------- helpers ----------
const asDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(+d) ? null : d;
};
const fmtDate = (v) => {
  const d = asDate(v);
  return d ? d.toLocaleDateString("en-GB") : "—";
};
const daysLeft = (end) => {
  const d = asDate(end);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};
const initials = (name = "") =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || "")
    .join("");

const StatusChip = ({ validTo }) => {
  const left = daysLeft(validTo);
  if (left == null) return <Chip size="small" label="Unknown" />;
  if (left < 0) return <Chip size="small" color="error" label="Expired" />;
  if (left <= 30) return <Chip size="small" color="warning" label={`Expires in ${left}d`} />;
  return <Chip size="small" color="success" label="Active" />;
};

const Tile = ({ icon, title, children, right }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      border: "1px solid #E7ECF5",
      bgcolor: "#fff",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Box display="flex" alignItems="center" gap={1}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {right}
    </Box>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Paper>
);

const Row = ({ label, value, mono = false, icon = null }) => (
  <Box sx={{ display: "flex", alignItems: "center", py: 0.4 }}>
    <Box
      sx={{
        width: { xs: 120, sm: 160 },
        pr: 2,
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {icon}
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        ...(mono && {
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }),
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [userDoc, setUserDoc] = useState(null);
  const [companyDoc, setCompanyDoc] = useState(null);
  const [licenseFromLicenses, setLicenseFromLicenses] = useState(null);

  const SECTIONS = ["Company", "License", "Admin", "Sign-in & Security"];
  const [active, setActive] = useState(SECTIONS[0]);

  useEffect(() => {
    (async () => {
      try {
        const u = auth.currentUser;
        if (!u) {
          setErr("No user logged in.");
          setLoading(false);
          return;
        }
        const uSnap = await getDoc(doc(db, "users", u.uid));
        if (!uSnap.exists()) {
          setErr("User profile not found.");
          setLoading(false);
          return;
        }
        const uData = { id: u.uid, ...uSnap.data() };
        setUserDoc(uData);

        if (uData.companyId) {
          const cSnap = await getDoc(doc(db, "companies", uData.companyId));
          if (cSnap.exists()) setCompanyDoc({ id: cSnap.id, ...cSnap.data() });

          const hasCompanyLicense =
            cSnap.exists() &&
            (cSnap.data().licenseValidFrom || cSnap.data().licenseValidTo || cSnap.data().licenseKey);
          if (!hasCompanyLicense) {
            const qLic = query(collection(db, "licenses"), where("assignedCompanyId", "==", uData.companyId));
            const licSnap = await getDocs(qLic);
            if (!licSnap.empty) {
              setLicenseFromLicenses({ id: licSnap.docs[0].id, ...licSnap.docs[0].data() });
            }
          }
        }

        setLoading(false);
      } catch (e) {
        console.error("Profile load failed:", e);
        setErr("Failed to load Profile.");
        setLoading(false);
      }
    })();
  }, []);

  const license = useMemo(() => {
    const c = companyDoc || {};
    if (c.licenseKey || c.licenseValidFrom || c.licenseValidTo || c.licenseStatus) {
      return {
        licenseKey: c.licenseKey || "",
        status: c.licenseStatus || "",
        validFrom: c.licenseValidFrom || null,
        validTo: c.licenseValidTo || null,
        assignedAt: c.licenseAssignedAt || c.licenseUpdatedAt || c.createdAt || null,
      };
    }
    const l = licenseFromLicenses || {};
    return {
      licenseKey: l.licenseKey || "",
      status: l.status || "",
      validFrom: l.validFrom || null,
      validTo: l.validTo || null,
      assignedAt: l.assignedAt || l.createdAt || null,
    };
  }, [companyDoc, licenseFromLicenses]);

  if (loading) return <CircularProgress sx={{ display: "block", m: "48px auto" }} />;
  if (err) return <Alert severity="error" sx={{ mt: 3 }}>{err}</Alert>;

  const adminName = userDoc?.name || "—";
  const adminEmail = userDoc?.email || "—";
  const companyName = companyDoc?.companyName || userDoc?.companyName || "—";
  const companyId   = companyDoc?.companyId   || userDoc?.companyId   || "—";

  const renderSection = () => {
    switch (active) {
      case "Company":
        return (
          <Tile icon={<BusinessIcon />} title="Company" right={<Chip size="small" variant="outlined" label={companyName} />}>
            <Row label="Company Name" value={companyName} />
            <Row label="Company ID" value={companyId} mono />
            <Row label="Created" value={fmtDate(companyDoc?.createdAt)} />
          </Tile>
        );
      case "License":
        return (
          <Tile icon={<VpnKeyIcon />} title="License" right={<StatusChip validTo={license?.validTo} />}>
            <Row label="License Key" value={license?.licenseKey || "—"} mono />
            <Row label="Status" value={license?.status || "—"} />
            <Row label="Valid From" value={fmtDate(license?.validFrom)} />
            <Row label="Valid To" value={fmtDate(license?.validTo)} />
            <Row label="Assigned / Updated" value={fmtDate(license?.assignedAt)} />
          </Tile>
        );
      case "Admin":
        return (
          <Tile icon={<PersonIcon />} title="Admin">
            <Row label="Admin Name" value={adminName} />
            <Row label="Role" value={userDoc?.role || companyDoc?.role || "—"} />
            <Row label="Category" value={userDoc?.category || "—"} />
            <Divider sx={{ my: 1 }} />
            <Row label="Email" value={adminEmail} />
            <Row label="Company" value={companyName} />
            <Row label="Company ID" value={companyId} mono />
          </Tile>
        );
      case "Sign-in & Security":
        return (
          <Tile icon={<SecurityIcon />} title="Sign-in & Security">
            <Row icon={<EmailIcon fontSize="small" />} label="Email" value={adminEmail} />
            <Row label="Active" value={userDoc?.isActive ? "Yes" : "No"} />
            <Row label="Last Login" value={fmtDate(userDoc?.lastLoginAt)} />
            <Row label="Previous Login" value={fmtDate(userDoc?.previousLoginAt)} />
          </Tile>
        );
      default:
        return null;
    }
  };

  return (
    <Container sx={{ mt: 2, maxWidth: "1200px" }}>
      <Grid container spacing={3}>
        {/* LEFT: profile + nav list */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E7ECF5",
              bgcolor: "#fff",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: "#E8EEFF", color: "#233AEF", fontWeight: 600 }}>
                {initials(adminName || companyName)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {adminName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {adminEmail}
                </Typography>
              </Box>
            </Box>

            <List dense disablePadding>
              {SECTIONS.map((label) => {
                const selected = active === label;
                return (
                  <ListItemButton
                    key={label}
                    onClick={() => setActive(label)}
                    selected={selected}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      "&.Mui-selected": { bgcolor: "rgba(41,68,214,0.08)" },
                      "&:hover": { bgcolor: "rgba(41,68,214,0.04)" },
                    }}
                  >
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ variant: "body2", fontWeight: selected ? 600 : 500 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* RIGHT: header + selected tile */}
        <Grid item xs={12} md={9}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {active}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {active === "Company" && "Company identity and metadata."}
              {active === "License" && "Your WorkPulse license information."}
              {active === "Admin" && "Primary admin details for this account."}
              {active === "Sign-in & Security" && "Sign-in snapshot and activity."}
            </Typography>
          </Box>

          {renderSection()}
        </Grid>
      </Grid>
    </Container>
  );
}
